import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import toast from 'react-hot-toast';
import type { Booking } from '@shared/types';

export default function CalendarEditor() {
  const { id } = useParams<{ id: string }>();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [blockedDates, setBlockedDates] = useState<string[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);

  const { data: listing } = useQuery({
    queryKey: ['listing', id],
    queryFn: async () => {
      if (!id) return null;
      const docRef = doc(db, 'listings', id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        return { id: docSnap.id, ...data, calendar: data.calendar || { blocked: [], bookingsRef: [] } };
      }
      return null;
    },
  });

  const { data: listingBookings } = useQuery({
    queryKey: ['bookings', id],
    queryFn: async () => {
      if (!id) return [];
      const q = query(
        collection(db, 'bookings'),
        where('listingId', '==', id),
        where('status', 'in', ['pending', 'confirmed'])
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          checkinTimestamp: data.checkinTimestamp instanceof Timestamp ? data.checkinTimestamp.toDate() : data.checkinTimestamp,
          checkoutTimestamp: data.checkoutTimestamp instanceof Timestamp ? data.checkoutTimestamp.toDate() : data.checkoutTimestamp,
        };
      }) as Booking[];
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (listing) {
      setBlockedDates((listing as any).calendar?.blocked || []);
    }
  }, [listing]);

  useEffect(() => {
    if (listingBookings) {
      setBookings(listingBookings);
    }
  }, [listingBookings]);

  const toggleBlockDate = async (date: Date) => {
    if (!id) return;
    const dateStr = date.toISOString().split('T')[0];
    const newBlockedDates = blockedDates.includes(dateStr)
      ? blockedDates.filter((d) => d !== dateStr)
      : [...blockedDates, dateStr];

    setBlockedDates(newBlockedDates);

    try {
      const listingRef = doc(db, 'listings', id);
      await updateDoc(listingRef, {
        'calendar.blocked': newBlockedDates,
        updatedAt: new Date(),
      });
      toast.success('Calendar updated');
    } catch (error: any) {
      toast.error('Error updating calendar');
      setBlockedDates(blockedDates); // Revert on error
    }
  };

  const isDateBooked = (date: Date) => {
    return bookings.some((booking) => {
      const checkIn = booking.checkinTimestamp instanceof Date ? booking.checkinTimestamp : new Date(booking.checkinTimestamp);
      const checkOut = booking.checkoutTimestamp instanceof Date ? booking.checkoutTimestamp : new Date(booking.checkoutTimestamp);
      const dateStr = date.toISOString().split('T')[0];
      const checkInStr = checkIn.toISOString().split('T')[0];
      const checkOutStr = checkOut.toISOString().split('T')[0];
      return dateStr >= checkInStr && dateStr < checkOutStr;
    });
  };

  const isDateBlocked = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return blockedDates.includes(dateStr);
  };

  const dayClassName = (date: Date) => {
    if (isDateBooked(date)) {
      return 'bg-red-100 text-red-800 border-red-300';
    }
    if (isDateBlocked(date)) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    }
    return '';
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold mb-6">Calendar & Availability</h1>
      {listing && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-4">{(listing as any).title || 'Listing Calendar'}</h2>
            <div className="flex gap-4 mb-4">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-red-100 border border-red-300 rounded mr-2"></div>
                <span className="text-sm">Booked</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-yellow-100 border border-yellow-300 rounded mr-2"></div>
                <span className="text-sm">Blocked</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-white border border-gray-300 rounded mr-2"></div>
                <span className="text-sm">Available</span>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Click on a date to block/unblock it. Booked dates cannot be unblocked.
            </p>
          </div>

          <DatePicker
            selected={selectedDate}
            onChange={(date) => {
              setSelectedDate(date);
              if (date && !isDateBooked(date)) {
                toggleBlockDate(date);
              }
            }}
            inline
            minDate={new Date()}
            dayClassName={dayClassName}
            highlightDates={bookings.flatMap((b) => {
              const checkIn = b.checkinTimestamp instanceof Date ? b.checkinTimestamp : new Date(b.checkinTimestamp);
              const checkOut = b.checkoutTimestamp instanceof Date ? b.checkoutTimestamp : new Date(b.checkoutTimestamp);
              const days = Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));
              return Array.from({ length: days }, (_, i) => {
                const d = new Date(checkIn);
                d.setDate(d.getDate() + i);
                return d;
              });
            })}
          />

          {bookings.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-4">Upcoming Bookings</h3>
              <div className="space-y-2">
                {bookings.map((booking) => (
                  <div key={booking.id} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                    <div>
                      <div className="font-medium">
                        {(booking.checkinTimestamp instanceof Date ? booking.checkinTimestamp : new Date(booking.checkinTimestamp)).toLocaleDateString('en-ZA')} -{' '}
                        {(booking.checkoutTimestamp instanceof Date ? booking.checkoutTimestamp : new Date(booking.checkoutTimestamp)).toLocaleDateString('en-ZA')}
                      </div>
                      <div className="text-sm text-gray-600">
                        Status: {booking.status} | R {(booking.totalCents / 100).toFixed(2)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

